import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { hero, images, contact } from '../../content';
import { getWhatsAppLink } from '../../content/contentUtils';

// Valores por defecto
const defaultHero = {
  title: "Recibe el apoyo que necesitas,",
  titleHighlight: "justo cuando lo necesitas",
  subtitle: "Créditos flexibles, seguros y a tu medida.",
  features: ["Aprobación en 24 hrs", "Asesoría gratuita"],
  primaryButton: { text: "Solicita tu crédito ahora", whatsappMessage: "Hola, me interesa solicitar un crédito" },
  secondaryButton: { text: "Conoce nuestros créditos", scrollTo: "creditos" },
  creditCard: { title: "Tu crédito aprobado", label: "Monto disponible", amount: "$150,000", successTitle: "¡Felicidades!", successText: "Crédito aprobado" }
};

export const HeroSection = () => {
  // Obtener contenido con fallbacks
  const content = {
    title: hero?.title || defaultHero.title,
    titleHighlight: hero?.titleHighlight || defaultHero.titleHighlight,
    subtitle: hero?.subtitle || defaultHero.subtitle,
    features: hero?.features || defaultHero.features,
    primaryButton: hero?.primaryButton || defaultHero.primaryButton,
    secondaryButton: hero?.secondaryButton || defaultHero.secondaryButton,
    creditCard: hero?.creditCard || defaultHero.creditCard
  };

  const heroImage = images?.hero || "/images/hero-imagen.png";
  const whatsappNumber = contact?.whatsapp?.number || "526561975200";

  const handleScrollToCredits = (e) => {
    e.preventDefault();
    const element = document.getElementById(content.secondaryButton.scrollTo || 'creditos');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#013C5D] via-[#025a8a] to-[#013C5D]">
        {/* Decorative elements */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#CFA935]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#CFA935]/5 rounded-full blur-3xl" />
        
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Hero Image/Visual - First on mobile */}
          <div className="relative order-1 lg:order-2">
            <div className="relative max-w-md mx-auto lg:max-w-none">
              {/* Main card - Larger size */}
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-4 sm:p-6 border border-white/20 shadow-2xl">
                <img
                  src={heroImage}
                  alt="Emprendedora exitosa"
                  className="rounded-2xl w-full h-[280px] sm:h-[350px] lg:h-[420px] object-cover object-top"
                />
                <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
                  <h3 className="text-white text-lg sm:text-xl font-semibold">{content.creditCard.title}</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/60 text-xs sm:text-sm">{content.creditCard.label}</p>
                      <p className="text-[#CFA935] text-xl sm:text-2xl font-bold">{content.creditCard.amount}</p>
                    </div>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#CFA935]/20 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-[#CFA935]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating card */}
              <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-[#013C5D] font-semibold text-sm sm:text-base">{content.creditCard.successTitle}</p>
                    <p className="text-gray-500 text-xs sm:text-sm">{content.creditCard.successText}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content - Second on mobile */}
          <div className="space-y-8 order-2 lg:order-1 mt-8 lg:mt-0">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {content.title}{' '}
              <span className="text-[#CFA935]">{content.titleHighlight}</span>
            </h1>

            <p className="text-xl text-white/80 max-w-xl leading-relaxed">
              {content.subtitle}
            </p>

            {/* Features list - Enlarged */}
            <div className="flex flex-wrap gap-4">
              {content.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 text-white/90 bg-white/10 px-5 py-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                  <CheckCircle className="w-7 h-7 text-[#CFA935]" />
                  <span className="text-base font-semibold">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={getWhatsAppLink(content.primaryButton.whatsappMessage)} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  className="bg-[#CFA935] hover:bg-[#b8942f] text-[#013C5D] font-semibold px-8 h-14 text-base shadow-xl hover:shadow-2xl transition-all group w-full"
                >
                  {content.primaryButton.text}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a 
                href={`#${content.secondaryButton.scrollTo}`}
                onClick={handleScrollToCredits}
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white hover:text-[#013C5D] font-semibold px-8 h-14 text-base backdrop-blur-sm transition-all w-full"
                >
                  {content.secondaryButton.text}
                </Button>
              </a>
            </div>

          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 rounded-full bg-white/50 animate-pulse" />
        </div>
      </div>
    </section>
  );
};
